import { useQuery } from '@tanstack/react-query';
import { repositorioSensores } from '../../../data/repositories/RepositorioSensores';
import { repositorioMedidores } from '../../../data/repositories/RepositorioMedidores';
import { repositorioActuadores } from '../../../data/repositories/RepositorioActuadores';
import type { Sensor } from '../../../domain/entities/Sensor';
import type { OrigenValor } from '../../../domain/entities/PanelPersonal';

/** Lo que el panel necesita saber de cualquier cosa que muestre. */
export interface ValorPanel {
  /** Lo que se lee en grande: «23.4 °C», «Encendido», «Caído». */
  texto: string;
  /** Merece atención: se pinta en rojo y el marcador cambia de color. */
  enAlarma: boolean;
  /** Hay dato de verdad. En falso el panel lo dice en vez de inventar un cero. */
  conDatos: boolean;
  /** Unidad, cuando la hay, para escribirla más pequeña al lado. */
  unidad: string;
}

const SIN_DATOS: ValorPanel = {
  texto: 'Sin datos',
  enAlarma: false,
  conDatos: false,
  unidad: '',
};

function deSensor(sensor: Sensor | null): ValorPanel {
  if (!sensor) return SIN_DATOS;

  const texto = sensor.valorNumericoMostrable;
  if (texto === '—') return SIN_DATOS;

  return {
    texto,
    enAlarma: sensor.estado.valor === 'critico',
    conDatos: true,
    unidad: sensor.unidadMostrable,
  };
}

/**
 * Resuelve el valor de cada elemento del panel, venga de donde venga.
 *
 * Un panel mezcla cosas que viven en sitios distintos: un sensor tiene lectura
 * y umbral, un nodo tiene latido, un medidor tiene magnitudes eléctricas y un
 * actuador tiene estado. Aquí se piden todos a la vez y se traducen a la misma
 * forma, para que la pantalla no tenga que saber de dónde salió cada número.
 *
 * Cada fuente se pide sólo si el panel la usa: un panel de puros sensores no
 * carga medidores.
 */
export function useValoresPanel(origenes: {
  sensores: readonly number[];
  nodos: readonly number[];
  medidores: readonly number[];
  actuadores: readonly number[];
}) {
  const sensores = useQuery({
    queryKey: ['sensores', {}],
    queryFn: () => repositorioSensores.listarConValores({}),
    enabled: origenes.sensores.length > 0,
  });

  const presencia = useQuery({
    queryKey: ['presencia-nodos', origenes.nodos],
    queryFn: () => repositorioSensores.presenciaDeNodos(origenes.nodos),
    enabled: origenes.nodos.length > 0,
    // La conexión cambia sola con el latido, sin que nadie toque nada.
    refetchInterval: 30_000,
  });

  const medidores = useQuery({
    queryKey: ['medidores', 'panel'],
    queryFn: () => repositorioMedidores.listar(),
    enabled: origenes.medidores.length > 0,
    refetchInterval: 60_000,
  });

  const actuadores = useQuery({
    queryKey: ['actuadores', 'panel'],
    queryFn: () => repositorioActuadores.listar({}),
    enabled: origenes.actuadores.length > 0,
    refetchInterval: 30_000,
  });

  /** El sensor completo, que el panel usa además para su nombre y su área. */
  const sensorDe = (id: number | null): Sensor | null =>
    id === null ? null : (sensores.data?.find((s) => s.id === id) ?? null);

  const valorDe = (origen: OrigenValor, referencia: string | null): ValorPanel => {
    if (!referencia) return SIN_DATOS;

    // Sensores, nodos y actuadores se numeran; los medidores usan UUID.
    const id = Number(referencia);

    if (origen === 'sensor') return deSensor(sensorDe(Number.isFinite(id) ? id : null));

    if (origen === 'nodo') {
      const nodo = presencia.data?.get(id);
      if (!nodo) return SIN_DATOS;

      /*
       * Sin vigilancia no se puede decir si está caído: el nodo puede llevar
       * horas callado porque no tiene nada que contar. Decir «desconectado»
       * ahí sería inventarse una avería.
       */
      if (!nodo.vigilaConexion) {
        return { texto: 'Sin vigilar', enAlarma: false, conDatos: true, unidad: '' };
      }

      if (nodo.enLinea === null) return SIN_DATOS;

      return {
        texto: nodo.enLinea ? 'Conectado' : 'Caído',
        enAlarma: !nodo.enLinea,
        conDatos: true,
        unidad: '',
      };
    }

    if (origen === 'medidor') {
      const medidor = medidores.data?.find((m) => m.id === referencia);
      if (!medidor) return SIN_DATOS;

      /*
       * La potencia total, que la entidad ya normaliza: cada protocolo nombra
       * sus variables a su manera y esa traducción no debe repetirse aquí.
       */
      const potencia = medidor.potenciaTotal;
      if (potencia === null) return SIN_DATOS;

      return {
        texto: potencia.toLocaleString('es'),
        enAlarma: false,
        conDatos: true,
        unidad: 'W',
      };
    }

    // Actuador: encendido o apagado.
    const actuador = actuadores.data?.find((a) => a.id === id);
    if (!actuador) return SIN_DATOS;

    if (actuador.encendido === null) return SIN_DATOS;

    return {
      texto: actuador.encendido ? 'Encendido' : 'Apagado',
      /*
       * Encendido se pinta en rojo y apagado en su color normal.
       *
       * No es que encender sea un fallo: es que en un panel de vigilancia lo
       * que se quiere cazar de un vistazo es qué está en marcha —una bomba, una
       * sirena, un sistema contra incendios—, y el rojo es lo que salta a la
       * vista sin buscarlo.
       */
      enAlarma: actuador.encendido === true,
      conDatos: true,
      unidad: '',
    };
  };

  return { valorDe, sensorDe, sensores: sensores.data ?? [] };
}
